import { Array, type Cause, Data, Match, Option } from 'effect';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';

import type { Page } from '@vs-shop/domain/pagination';
import type { ProductSummary } from '../../capability/productSchema';

export type ListingPages<E> = Data.TaggedEnum<{
  Pending: {};
  Failed: { readonly cause: Cause.Cause<E>; readonly waiting: boolean };
  Available: {
    readonly products: Page<ProductSummary>;
    readonly waiting: boolean;
    readonly failure: Option.Option<Cause.Cause<E>>;
    readonly failedPage: Option.Option<number>;
  };
}>;

/** Collects coherent pages and distinguishes refresh failures from failed append reads. */
export const collectListingPages = <E>(options: {
  readonly firstPage: number;
  readonly requestedPage: number;
  readonly pageMode: 'append' | 'replace';
  readonly read: (page: number) => AsyncResult.AsyncResult<Page<ProductSummary>, E>;
}): ListingPages<E> => {
  const Outcome = Data.taggedEnum<ListingPages<E>>();
  const pages: Array<Page<ProductSummary>> = [];
  let waiting = false;
  let failure: Option.Option<Cause.Cause<E>> = Option.none();
  let failedPage: Option.Option<number> = Option.none();
  const available = (): ListingPages<E> =>
    Array.match(pages, {
      onEmpty: () => Outcome.Pending(),
      onNonEmpty: (loaded) => {
        const first = Array.headNonEmpty(loaded);
        const latest = Array.lastNonEmpty(loaded);
        return Outcome.Available({
          products: Match.value(loaded.length).pipe(
            Match.when(1, () => first),
            Match.orElse(() => ({
              items: loaded.flatMap((page) => page.items),
              total: latest.total,
              page: latest.page,
              itemsPerPage: latest.itemsPerPage,
              lastPage: latest.lastPage,
            }))
          ),
          waiting,
          failure,
          failedPage,
        });
      },
    });
  let stopped: Option.Option<ListingPages<E>> = Option.none();
  for (
    let page = options.firstPage;
    page <= options.requestedPage && Option.isNone(stopped);
    page += 1
  ) {
    const result = options.read(page);
    stopped = AsyncResult.match(result, {
      onInitial: () => Option.some(Outcome.Pending()),
      onFailure: (failed) =>
        Option.match(AsyncResult.value(failed), {
          onSome: (previous) => {
            pages.push(previous);
            failure = Option.orElse(failure, () => Option.some(failed.cause));
            waiting ||= failed.waiting;
            return Option.none();
          },
          onNone: () =>
            Match.value(options.pageMode).pipe(
              Match.when('append', () =>
                Array.match(pages, {
                  onEmpty: () =>
                    Option.some(Outcome.Failed({ cause: failed.cause, waiting: failed.waiting })),
                  onNonEmpty: () => {
                    failedPage = Option.some(page);
                    waiting ||= failed.waiting;
                    return Option.some(available());
                  },
                })
              ),
              Match.when('replace', () =>
                Option.some(Outcome.Failed({ cause: failed.cause, waiting: failed.waiting }))
              ),
              Match.exhaustive
            ),
        }),
      onSuccess: (success) => {
        pages.push(success.value);
        waiting ||= success.waiting;
        return Option.fromUndefinedOr(success.value.lastPage).pipe(
          Option.filter((lastPage) => page >= lastPage),
          Option.map(available)
        );
      },
    });
  }
  return Option.getOrElse(stopped, available);
};
